import { LoadingSpinner as BaseLoadingSpinner, FullPageError } from "./common/components";
import { BookingData, BookingTool } from "./AppDataProvider/AppDataProvider";
import React, { ReactElement } from "react";
import { UnitOfMeasurement, useGetOrganisationQuery } from "./GeneratedGraphQL";
import { absoluteCentre } from "./common/styles";
import { ContentContainer } from "./ContentContainer";
import { getMicrositeResourceSelectionMode } from "./utils";
import { resolveEffectiveBrand } from "./utils";
import styled from "styled-components";
import { useClientLocation } from "./utils";
type GenericBookingToolDataProps = {
  inPopoverMode?: boolean;
};
export function GenericBookingToolData({
  inPopoverMode
}: GenericBookingToolDataProps): ReactElement {
  const {
    location,
    timezone: clientTimezone
  } = useClientLocation({
    fallbackLocation: "GB",
    fallbackTimezone: "Europe/London"
  });
  const urlParams = new URLSearchParams(window.location.search);
  const organisationId = urlParams.get("organisationId") ?? "";
  const preFillResourceId = urlParams.get("resourceId");
  const createdSource = urlParams.get("createdSource");
  const {
    data,
    loading,
    error,
    fetchMore
  } = useGetOrganisationQuery({
    variables: {
      id: organisationId,
      serviceFilters: {
        ...(preFillResourceId && {
          resourceIds: [preFillResourceId]
        }),
        visibleOnly: true
      }
    }
  });
  if (loading) return <LoadingSpinner />;
  if (error || !data?.organisation) return <FullPageError />;
  const organisation = data.organisation;
  const services = data.organisation.services;
  if (services && services.pageInfo.hasNextPage) {
    void fetchMore({
      variables: {
        id: organisationId,
        ...(preFillResourceId && {
          serviceFilters: {
            resourceIds: [preFillResourceId]
          }
        }),
        serviceCursor: services.pageInfo.endCursor
      }
    });
  }

  /**
   * It's possible we are still fetching the next pages
   * of the organisation.services graphs.
   *
   * If that is the case return the loading content
   */
  if (services && services.pageInfo.hasNextPage) {
    return <LoadingSpinner />;
  }
  let bookingData: BookingData = {
    createdSource: createdSource ? createdSource : "bookingapp",
    organisation: organisation,
    sessionId: Math.random().toString(12).substring(2, 9),
    timezone: organisation.forceTimezone === "AUTO_DETECT" ? clientTimezone : organisation.forceTimezone
  };

  /**
   * If there is only 1 service we need to use the id of that single service.
   * This will be required when the service selection step is disabled.
   *
   * Also, if the service only allows one attendee, or only has one duration,
   * we also need to add those values directly to the bookingData.
   */
  if (services && services.edges.length === 1) {
    const service = services.edges[0].node;
    const {
      durations,
      maxParticipantsPerBooking
    } = service;
    bookingData = {
      ...bookingData,
      service,
      ...(durations.length === 1 && {
        duration: durations[0]
      }),
      ...((maxParticipantsPerBooking ?? 1) === 1 && {
        attendees: 1
      })
    };
  }
  const bookingTool: BookingTool = {
    _id: "GENERIC_BOOKING_TOOL",
    customColours: {
      primary: organisation.micrositeColours.primary,
      secondary: organisation.micrositeColours.secondary
    },
    /**
     * There is no BookingTool at all in this generic (organisationId-only)
     * embed path, so `bookingToolBrand` is omitted — this falls through to
     * the new org-level `brandConfig` (Phase 1 of org-level brand
     * configuration), then to the legacy `micrositeColours`, then to
     * static defaults. See resolveEffectiveBrand for the per-field
     * cascading fallback logic.
     */
    brand: resolveEffectiveBrand({
      orgBrandConfig: organisation.brandConfig,
      legacyMicrositeColours: organisation.micrositeColours
    }),
    forceOrganisationSelection: false,
    /**
     * If we have not been provided a serviceId in the url params,
     * we want to set forceServiceSelection true.
     */
    forceServiceSelection: urlParams.get("serviceId") ? false : true,
    hideAppointeddFooter: data.organisation.hideAppointeddFooter,
    isGenericBookingTool: true,
    name: "Microsite Widget",
    organisationAvailabilityFlow: false,
    organisations: {
      edges: [{
        cursor: organisationId,
        node: organisation
      }],
      pageInfo: {
        endCursor: null,
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: null,
        total: 1
      }
    },
    resourceSelectionMode: getMicrositeResourceSelectionMode({
      allowResourceGroupSelection: organisation.allowResourceGroupSelection,
      allowResourceSelection: organisation.allowResourceSelection
    }),
    services,
    // TODO - future PR need to decide where this is set
    unitOfMeasurement: UnitOfMeasurement.Kilometres
  };
  return <ContentContainer bookingData={bookingData} bookingTool={bookingTool} inPopoverMode={inPopoverMode} userLocation={location} data-sentry-element="ContentContainer" data-sentry-component="GenericBookingToolData" data-sentry-source-file="GenericBookingToolData.tsx" />;
}
const LoadingSpinner = styled(BaseLoadingSpinner)`
  ${absoluteCentre}
`;
export default GenericBookingToolData;